import { Button, Flex, Grid, SimpleInput, Typography, colors } from '@aloft/toolkit-sui';

import { useState } from 'react';
import { useParams } from 'react-router';

import { VacantLandSalesMap } from '../../../components/VacantLandSales/VacantLandSalesMap.tsx';
import { VacantLandSalesTable } from '../../../components/VacantLandSales/VacantLandSalesTable.tsx';
import { DistributionOfVacantLandGraph } from '../../../components/graphs/costApproach/DistributionOfVacantLandGraph.tsx';
import { SiteExtractionGraph } from '../../../components/graphs/costApproach/SiteExtractionGraph.tsx';
import { SiteSizeVsSiteValueGraph } from '../../../components/graphs/costApproach/SiteSizeVsSiteValueGraph.tsx';
import { SiteToValueRatioGraph } from '../../../components/graphs/costApproach/SiteToValueRatioGraph.tsx';
import { VacantLandCostPerSFVsSiteSizeGraph } from '../../../components/graphs/costApproach/VacantLandCostPerSFVsSiteSizeGraph.tsx';
import {
  getVacantLandChartData,
  getVacantLandSalesExponentialFit,
} from '../../../components/graphs/costApproach/utils.ts';
import { useVacantLandSales } from '../../../hooks/useVacantLandSales.tsx';
import { selectPropertyIdentificationById } from '../../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';

const VacantLandSalesMapTable = ({
  vacantLandSales,
}: {
  vacantLandSales: ReturnType<typeof useVacantLandSales>;
}) => {
  const orderID = useParams().orderId!;
  const subjectPropertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, 'subject'),
  );
  const subjectCoordinates = subjectPropertyIdentification?.coordinates;

  const [searchRadius, setSearchRadius] = useState<number>(5);
  const [focusedRow, setFocusedRow] = useState<string | undefined>(undefined);
  const [view, setView] = useState<'map' | 'table'>('table');

  if (!vacantLandSales || !subjectCoordinates) return null;

  const vacantLandSalesWithinRadius = vacantLandSales.filter(
    ({ distanceToSubject }) => distanceToSubject <= searchRadius,
  );

  const focusedPropertyIndex = vacantLandSalesWithinRadius.findIndex(({ id }) => id === focusedRow);
  const focusedPropertyId =
    focusedPropertyIndex !== -1 ? vacantLandSalesWithinRadius[focusedPropertyIndex]?.id : undefined;

  return (
    <Flex
      flexDirection="column"
      gap="1rem"
      height="350px"
      backgroundColor="surface1"
      padding="1rem"
      overflow="hidden"
      borderRadius="8px"
    >
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="center" gap="1rem">
          <Typography variant="h3">Vacant Land Sales</Typography>
          <Flex alignItems="center">
            <Typography variant="h3" color="textTertiary">
              Sales Within:&nbsp;
            </Typography>
            <SimpleInput
              type="number"
              width="2rem"
              value={searchRadius.toString()}
              onChange={(e) => setSearchRadius(Number(e.target.value))}
            />
            <Typography variant="h3" color="textTertiary">
              &nbsp;mile(s)
            </Typography>
          </Flex>
        </Flex>
        <Flex alignItems="center" gap="0.5rem">
          <Button
            variant="inlineSecondary"
            onClick={() => setView('table')}
            disabled={view === 'table'}
            text="Table"
            __dangerDoNotUseSx={{
              color: view === 'table' ? colors.textPrimary : colors.textTertiary,
            }}
          />
          <Button
            variant="inlineSecondary"
            onClick={() => setView('map')}
            disabled={view === 'map'}
            text="Map"
            __dangerDoNotUseSx={{
              color: view === 'map' ? colors.textPrimary : colors.textTertiary,
            }}
          />
        </Flex>
      </Flex>
      <Flex flexGrow={1} width="100%" height="100%">
        {view === 'map' && (
          <VacantLandSalesMap
            subjectCoordinates={subjectCoordinates}
            vacantLandSales={vacantLandSalesWithinRadius}
            focusedPropertyId={focusedPropertyId}
            setFocusedPropertyId={setFocusedRow}
          />
        )}
        {view === 'table' && (
          <VacantLandSalesTable
            orderID={orderID}
            vacantLandSales={vacantLandSalesWithinRadius}
            focusedRowIndex={focusedPropertyIndex}
            onRowClick={(row) => setFocusedRow(row.id)}
            rowsPerPage={3}
          />
        )}
      </Flex>
    </Flex>
  );
};

export const AppraisifyAnalysisGraphs = () => {
  const vacantLandSales = useVacantLandSales();
  const vacantLandSalesChartData = getVacantLandChartData(vacantLandSales);
  if (!vacantLandSalesChartData) return null;

  const vacantLandSalesExponentialFit = getVacantLandSalesExponentialFit(vacantLandSalesChartData);

  return (
    <Grid gridTemplateColumns="1fr 1fr" padding="1rem" gap="1rem" backgroundColor="surface2">
      <SiteSizeVsSiteValueGraph disableAnimations={false} />
      <VacantLandCostPerSFVsSiteSizeGraph
        disableAnimations={false}
        vacantLandSalesChartData={vacantLandSalesChartData}
        vacantLandSalesExponentialFit={vacantLandSalesExponentialFit}
      />
      <SiteToValueRatioGraph disableAnimations={false} />
      <DistributionOfVacantLandGraph
        disableAnimations={false}
        vacantLandSalesChartData={vacantLandSalesChartData}
      />
      <SiteExtractionGraph
        disableAnimations={false}
        vacantLandSalesExponentialFit={vacantLandSalesExponentialFit}
      />
      <VacantLandSalesMapTable vacantLandSales={vacantLandSales} />
    </Grid>
  );
};
